<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="800px"
    persistent
    scrollable
  >
    <v-card class="user-form-dialog">
      <!-- Header del diálogo -->
      <v-card-title class="bg-primary text-white pa-4">
        <div class="d-flex align-center">
          <v-icon size="28" class="mr-3">
            {{ isEdit ? 'mdi-account-edit' : 'mdi-account-plus' }}
          </v-icon>
          <div>
            <h3 class="text-h6 font-weight-bold">
              {{ isEdit ? 'Editar Usuario' : 'Nuevo Usuario' }}
            </h3>
            <p class="text-body-2 mb-0 text-grey-lighten-2">
              {{ isEdit ? 'Actualiza la información del usuario' : 'Crea un nuevo usuario en el sistema' }}
            </p>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="handleCancel"
            :disabled="isSubmitting"
          ></v-btn>
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <FormValidation
          v-model="formData"
          :rules="validationRules"
          :show-validation-summary="true"
          :validate-on-change="true"
          @validation-change="onValidationChange"
          ref="formValidator"
        >
          <template #default="{ errors, hasError, setFieldValue, getFieldValue }">
            <v-form ref="vForm" @submit.prevent="handleSubmit" class="pa-6">
              <v-row>
                <!-- Avatar y información básica -->
                <v-col cols="12" class="text-center mb-4">
                  <div class="avatar-upload-section">
                    <v-avatar
                      size="120"
                      class="mb-4 avatar-preview"
                      :class="{ 'avatar-hover': !avatarPreview }"
                      @click="$refs.avatarInput?.click()"
                    >
                      <v-img
                        v-if="avatarPreview"
                        :src="avatarPreview"
                        alt="Avatar preview"
                        cover
                      ></v-img>
                      <v-icon
                        v-else
                        size="60"
                        color="grey-lighten-1"
                      >
                        mdi-account-circle
                      </v-icon>
                    </v-avatar>
                    
                    <input
                      ref="avatarInput"
                      type="file"
                      accept="image/*"
                      style="display: none"
                      @change="onAvatarSelect"
                    />
                    
                    <div class="avatar-actions">
                      <v-btn
                        variant="outlined"
                        size="small"
                        prepend-icon="mdi-camera"
                        @click="$refs.avatarInput?.click()"
                        class="mr-2"
                      >
                        Subir Foto
                      </v-btn>
                      <v-btn
                        v-if="avatarPreview"
                        variant="outlined"
                        size="small"
                        color="error"
                        icon="mdi-delete"
                        @click="removeAvatar"
                      ></v-btn>
                    </div>
                  </div>
                </v-col>

                <!-- Información personal -->
                <v-col cols="12">
                  <v-card variant="outlined" class="mb-4">
                    <v-card-title class="bg-grey-lighten-4 text-h6">
                      <v-icon class="mr-2">mdi-account</v-icon>
                      Información Personal
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <v-row>
                        <!-- Nombre -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('firstName')"
                            @update:model-value="(val) => setFieldValue('firstName', val)"
                            label="Nombre *"
                            placeholder="Ingresa el nombre"
                            variant="outlined"
                            :error="hasError('firstName')"
                            :error-messages="errors.firstName"
                            prepend-inner-icon="mdi-account"
                            @blur="markTouched('firstName')"
                          ></v-text-field>
                        </v-col>

                        <!-- Apellido -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('lastName')"
                            @update:model-value="(val) => setFieldValue('lastName', val)"
                            label="Apellido *"
                            placeholder="Ingresa el apellido"
                            variant="outlined"
                            :error="hasError('lastName')"
                            :error-messages="errors.lastName"
                            prepend-inner-icon="mdi-account"
                            @blur="markTouched('lastName')"
                          ></v-text-field>
                        </v-col>

                        <!-- Email -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('email')"
                            @update:model-value="(val) => setFieldValue('email', val)"
                            label="Correo electrónico *"
                            placeholder="usuario@empresa.com"
                            variant="outlined"
                            type="email"
                            :error="hasError('email')"
                            :error-messages="errors.email"
                            prepend-inner-icon="mdi-email"
                            @blur="markTouched('email')"
                          ></v-text-field>
                        </v-col>

                        <!-- Teléfono -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('phone')"
                            @update:model-value="(val) => setFieldValue('phone', val)"
                            label="Teléfono"
                            placeholder="999-999-9999"
                            variant="outlined"
                            :error="hasError('phone')"
                            :error-messages="errors.phone"
                            prepend-inner-icon="mdi-phone"
                            @blur="markTouched('phone')"
                          ></v-text-field>
                        </v-col>

                        <!-- DNI/Documento -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('documentNumber')"
                            @update:model-value="(val) => setFieldValue('documentNumber', val)"
                            label="Número de documento"
                            placeholder="DNI, Pasaporte, etc."
                            variant="outlined"
                            :error="hasError('documentNumber')"
                            :error-messages="errors.documentNumber"
                            prepend-inner-icon="mdi-card-account-details"
                            @blur="markTouched('documentNumber')"
                          ></v-text-field>
                        </v-col>

                        <!-- Fecha de nacimiento -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('birthDate')"
                            @update:model-value="(val) => setFieldValue('birthDate', val)"
                            label="Fecha de nacimiento"
                            type="date"
                            variant="outlined"
                            :error="hasError('birthDate')"
                            :error-messages="errors.birthDate"
                            prepend-inner-icon="mdi-calendar"
                            @blur="markTouched('birthDate')"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Información del sistema -->
                <v-col cols="12">
                  <v-card variant="outlined" class="mb-4">
                    <v-card-title class="bg-grey-lighten-4 text-h6">
                      <v-icon class="mr-2">mdi-cog</v-icon>
                      Configuración del Sistema
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <v-row>
                        <!-- Usuario -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('username')"
                            @update:model-value="(val) => setFieldValue('username', val)"
                            label="Nombre de usuario *"
                            placeholder="usuario123"
                            variant="outlined"
                            :error="hasError('username')"
                            :error-messages="errors.username"
                            prepend-inner-icon="mdi-account-circle"
                            :readonly="isEdit"
                            :hint="isEdit ? 'El nombre de usuario no se puede cambiar' : 'Debe ser único en el sistema'"
                            persistent-hint
                            @blur="markTouched('username')"
                          ></v-text-field>
                        </v-col>

                        <!-- Contraseña -->
                        <v-col cols="12" md="6">
                          <v-text-field
                            :model-value="getFieldValue('password')"
                            @update:model-value="(val) => setFieldValue('password', val)"
                            :label="isEdit ? 'Nueva contraseña (opcional)' : 'Contraseña *'"
                            :placeholder="isEdit ? 'Dejar vacío para mantener actual' : 'Mínimo 8 caracteres'"
                            variant="outlined"
                            :type="showPassword ? 'text' : 'password'"
                            :error="hasError('password')"
                            :error-messages="errors.password"
                            prepend-inner-icon="mdi-lock"
                            :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append-inner="showPassword = !showPassword"
                            @blur="markTouched('password')"
                          ></v-text-field>
                        </v-col>

                        <!-- Confirmar contraseña -->
                        <v-col cols="12" md="6" v-if="getFieldValue('password')">
                          <v-text-field
                            :model-value="getFieldValue('confirmPassword')"
                            @update:model-value="(val) => setFieldValue('confirmPassword', val)"
                            label="Confirmar contraseña *"
                            placeholder="Repite la contraseña"
                            variant="outlined"
                            :type="showConfirmPassword ? 'text' : 'password'"
                            :error="hasError('confirmPassword')"
                            :error-messages="errors.confirmPassword"
                            prepend-inner-icon="mdi-lock-check"
                            :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append-inner="showConfirmPassword = !showConfirmPassword"
                            @blur="markTouched('confirmPassword')"
                          ></v-text-field>
                        </v-col>

                        <!-- Rol -->
                        <v-col cols="12" md="6">
                          <v-select
                            :model-value="getFieldValue('role')"
                            @update:model-value="(val) => setFieldValue('role', val)"
                            :items="roleOptions"
                            label="Rol del usuario *"
                            variant="outlined"
                            :error="hasError('role')"
                            :error-messages="errors.role"
                            prepend-inner-icon="mdi-shield-account"
                            @blur="markTouched('role')"
                          >
                            <template #item="{ props, item }">
                              <v-list-item v-bind="props" :title="item.title">
                                <template #prepend>
                                  <v-icon :color="item.raw.color">
                                    {{ item.raw.icon }}
                                  </v-icon>
                                </template>
                                <template #subtitle>
                                  <span class="text-caption">{{ item.raw.description }}</span>
                                </template>
                              </v-list-item>
                            </template>
                          </v-select>
                        </v-col>

                        <!-- Agencias asignadas (para ciertos roles) -->
                        <v-col cols="12" v-if="showAgencySelection">
                          <v-autocomplete
                            :model-value="getFieldValue('assignedAgencies')"
                            @update:model-value="(val) => setFieldValue('assignedAgencies', val)"
                            :items="agencyOptions"
                            label="Agencias asignadas"
                            placeholder="Selecciona las agencias que puede gestionar"
                            variant="outlined"
                            multiple
                            chips
                            closable-chips
                            :error="hasError('assignedAgencies')"
                            :error-messages="errors.assignedAgencies"
                            prepend-inner-icon="mdi-store-marker"
                            @blur="markTouched('assignedAgencies')"
                          >
                            <template #chip="{ props, item }">
                              <v-chip
                                v-bind="props"
                                :text="item.title"
                                size="small"
                                color="primary"
                                variant="flat"
                              ></v-chip>
                            </template>
                          </v-autocomplete>
                        </v-col>

                        <!-- Estado del usuario -->
                        <v-col cols="12" md="6">
                          <v-switch
                            :model-value="getFieldValue('isActive')"
                            @update:model-value="(val) => setFieldValue('isActive', val)"
                            label="Usuario activo"
                            color="success"
                            inset
                            :messages="getFieldValue('isActive') ? 'El usuario puede acceder al sistema' : 'El usuario no puede acceder al sistema'"
                          ></v-switch>
                        </v-col>

                        <!-- Notificaciones por email -->
                        <v-col cols="12" md="6">
                          <v-switch
                            :model-value="getFieldValue('emailNotifications')"
                            @update:model-value="(val) => setFieldValue('emailNotifications', val)"
                            label="Notificaciones por email"
                            color="info"
                            inset
                            messages="Recibir notificaciones importantes por correo"
                          ></v-switch>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Permisos especiales (solo para Super Admin) -->
                <v-col cols="12" v-if="canManagePermissions">
                  <v-card variant="outlined">
                    <v-card-title class="bg-warning-lighten-4 text-h6">
                      <v-icon class="mr-2">mdi-key</v-icon>
                      Permisos Especiales
                    </v-card-title>
                    <v-card-text class="pa-4">
                      <v-row>
                        <v-col cols="12" md="6" v-for="permission in permissionOptions" :key="permission.value">
                          <v-checkbox
                            :model-value="(getFieldValue('permissions') || []).includes(permission.value)"
                            @update:model-value="(checked) => togglePermission(permission.value, checked)"
                            :label="permission.title"
                            :messages="permission.description"
                            color="warning"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-form>
          </template>
        </FormValidation>
      </v-card-text>

      <!-- Acciones del diálogo -->
      <v-card-actions class="pa-4 bg-grey-lighten-5">
        <v-spacer></v-spacer>
        <v-btn
          variant="outlined"
          @click="handleCancel"
          :disabled="isSubmitting"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="handleSubmit"
          :disabled="!isFormValid || isSubmitting"
          :loading="isSubmitting"
        >
          {{ isEdit ? 'Actualizar Usuario' : 'Crear Usuario' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import FormValidation from './FormValidation.vue'

interface UserFormData {
  firstName: string
  lastName: string
  email: string
  phone?: string
  documentNumber?: string
  birthDate?: string
  username: string
  password?: string
  confirmPassword?: string
  role: string
  assignedAgencies?: string[]
  isActive: boolean
  emailNotifications: boolean
  permissions?: string[]
  avatar?: File
}

interface UserFormDialogProps {
  modelValue: boolean
  userData?: UserFormData
  isEdit?: boolean
  agencies?: Array<any>
  currentUserRole?: string
}

interface UserFormDialogEmits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'submit', data: UserFormData): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<UserFormDialogProps>(), {
  userData: () => ({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    role: '',
    isActive: true,
    emailNotifications: true
  }),
  isEdit: false,
  agencies: () => [],
  currentUserRole: 'USER'
})

const emit = defineEmits<UserFormDialogEmits>()

// Referencias
const formValidator = ref()
const vForm = ref()
const avatarInput = ref()

// Estado reactivo
const formData = ref<UserFormData>({ ...props.userData })
const isFormValid = ref(false)
const isSubmitting = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const avatarPreview = ref<string>('')

// Opciones de datos
const roleOptions = [
  {
    title: 'Usuario',
    value: 'USER',
    icon: 'mdi-account',
    color: 'blue',
    description: 'Acceso básico al sistema'
  },
  {
    title: 'Supervisor de Campo',
    value: 'FIELD_SUPERVISOR',
    icon: 'mdi-account-tie',
    color: 'green',
    description: 'Supervisa operaciones de campo'
  },
  {
    title: 'Administrador de Agencia',
    value: 'AGENCY_ADMIN',
    icon: 'mdi-store-cog',
    color: 'orange',
    description: 'Administra una o varias agencias'
  },
  {
    title: 'Ejecutivo',
    value: 'EXECUTIVE',
    icon: 'mdi-tie',
    color: 'purple',
    description: 'Acceso ejecutivo y reportes'
  },
  {
    title: 'Administrador',
    value: 'ADMINISTRATOR',
    icon: 'mdi-shield-account',
    color: 'red',
    description: 'Administrador del sistema'
  },
  {
    title: 'Super Administrador',
    value: 'SUPER_ADMIN',
    icon: 'mdi-shield-crown',
    color: 'deep-purple',
    description: 'Acceso completo al sistema'
  }
]

const agencyOptions = computed(() => {
  return props.agencies.map(agency => ({
    title: `${agency.name} - ${agency.code}`,
    value: agency.id,
    subtitle: agency.address
  }))
})

const permissionOptions = [
  {
    title: 'Gestionar Usuarios',
    value: 'MANAGE_USERS',
    description: 'Crear, editar y eliminar usuarios'
  },
  {
    title: 'Ver Reportes Financieros',
    value: 'VIEW_FINANCIAL_REPORTS',
    description: 'Acceso a reportes financieros confidenciales'
  },
  {
    title: 'Configurar Sistema',
    value: 'SYSTEM_CONFIG',
    description: 'Modificar configuraciones del sistema'
  },
  {
    title: 'Auditoría Completa',
    value: 'FULL_AUDIT',
    description: 'Ver logs completos de auditoría'
  }
]

// Reglas de validación dinámicas
const validationRules = computed(() => {
  const rules: any = {
    firstName: [
      { required: true, message: 'El nombre es requerido' },
      { minLength: 2, message: 'El nombre debe tener al menos 2 caracteres' }
    ],
    lastName: [
      { required: true, message: 'El apellido es requerido' },
      { minLength: 2, message: 'El apellido debe tener al menos 2 caracteres' }
    ],
    email: [
      { required: true, message: 'El email es requerido' },
      { email: true, message: 'Ingresa un email válido' }
    ],
    username: [
      { required: true, message: 'El nombre de usuario es requerido' },
      { minLength: 3, message: 'Mínimo 3 caracteres' },
      { 
        pattern: /^[a-zA-Z0-9_]+$/, 
        message: 'Solo letras, números y guiones bajos' 
      }
    ],
    role: [
      { required: true, message: 'El rol es requerido' }
    ],
    phone: [
      { 
        pattern: /^[\d\-\+\(\)\s]+$/, 
        message: 'Formato de teléfono inválido' 
      }
    ]
  }

  // Validación de contraseña solo si no es edición o si se ingresó nueva contraseña
  if (!props.isEdit || formData.value.password) {
    rules.password = [
      { required: !props.isEdit, message: 'La contraseña es requerida' },
      { minLength: 8, message: 'Mínimo 8 caracteres' },
      { 
        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 
        message: 'Debe contener mayúscula, minúscula y número' 
      }
    ]

    rules.confirmPassword = [
      { 
        required: !!formData.value.password, 
        message: 'Confirma la contraseña' 
      },
      { 
        custom: (value: string) => value === formData.value.password,
        message: 'Las contraseñas no coinciden'
      }
    ]
  }

  return rules
})

// Computed
const showAgencySelection = computed(() => {
  return ['FIELD_SUPERVISOR', 'AGENCY_ADMIN'].includes(formData.value.role)
})

const canManagePermissions = computed(() => {
  return props.currentUserRole === 'SUPER_ADMIN'
})

// Watchers
watch(() => props.userData, (newValue) => {
  formData.value = { ...newValue }
  // Cargar avatar si existe
  if (newValue.avatar) {
    // Aquí se podría cargar la imagen del usuario existente
  }
}, { deep: true })

watch(() => formData.value.role, (newRole) => {
  // Limpiar agencias asignadas si el rol no las requiere
  if (!showAgencySelection.value) {
    formData.value.assignedAgencies = []
  }
})

// Métodos
const onValidationChange = (valid: boolean) => {
  isFormValid.value = valid
}

const markTouched = (field: string) => {
  formValidator.value?.markFieldAsTouched(field)
}

const onAvatarSelect = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (file) {
    // Validar tipo y tamaño
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen')
      return
    }
    
    if (file.size > 2 * 1024 * 1024) { // 2MB
      alert('La imagen no puede ser mayor a 2MB')
      return
    }
    
    // Crear preview
    const reader = new FileReader()
    reader.onload = (e) => {
      avatarPreview.value = e.target?.result as string
    }
    reader.readAsDataURL(file)
    
    formData.value.avatar = file
  }
}

const removeAvatar = () => {
  avatarPreview.value = ''
  formData.value.avatar = undefined
  if (avatarInput.value) {
    avatarInput.value.value = ''
  }
}

const togglePermission = (permission: string, checked: boolean) => {
  if (!formData.value.permissions) {
    formData.value.permissions = []
  }
  
  if (checked) {
    if (!formData.value.permissions.includes(permission)) {
      formData.value.permissions.push(permission)
    }
  } else {
    const index = formData.value.permissions.indexOf(permission)
    if (index > -1) {
      formData.value.permissions.splice(index, 1)
    }
  }
}

const handleSubmit = async () => {
  const isValid = await formValidator.value.validate()
  
  if (!isValid) {
    return
  }
  
  isSubmitting.value = true
  
  try {
    // Preparar datos para envío
    const submitData = { ...formData.value }
    
    // No enviar confirmPassword
    delete submitData.confirmPassword
    
    // Si es edición y no hay nueva contraseña, no enviar password
    if (props.isEdit && !submitData.password) {
      delete submitData.password
    }
    
    emit('submit', submitData)
  } finally {
    isSubmitting.value = false
  }
}

const handleCancel = () => {
  emit('cancel')
  emit('update:modelValue', false)
}
</script>

<style scoped>
.user-form-dialog {
  max-height: 90vh;
}

.avatar-upload-section {
  position: relative;
}

.avatar-preview {
  cursor: pointer;
  transition: opacity 0.3s ease;
  border: 3px solid rgba(0, 0, 0, 0.1);
}

.avatar-preview:hover,
.avatar-hover:hover {
  opacity: 0.8;
}

.avatar-actions {
  margin-top: 16px;
}

.v-card-title {
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>
